import {
  Directive,
  ElementRef,
  HostListener,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

@Directive({
  selector: '[placaVeiculoMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlacaVeiculoMaskDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class PlacaVeiculoMaskDirective implements ControlValueAccessor {
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  writeValue(value: string | null): void {
    const normalizedValue = this.normalize(value ?? '');
    const formattedValue = this.format(normalizedValue);

    this.el.nativeElement.value = formattedValue;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    const normalizedValue = this.normalize(input.value);
    const formattedValue = this.format(normalizedValue);

    input.value = formattedValue;

    this.onChange(normalizedValue);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  private normalize(value: string): string {
    return (value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 7);
  }

  private format(value: string): string {
    if (!value) return '';

    const letters = value.slice(0, 3).replace(/[^A-Z]/g, '');
    const rest = value.slice(3);

    if (letters.length < 3) {
      return letters;
    }

    if (!rest) {
      return letters;
    }

    const first = rest[0] ?? '';
    const second = rest[1] ?? '';
    const third = rest[2] ?? '';
    const fourth = rest[3] ?? '';

    const firstIsDigit = /\d/.test(first);

    if (!firstIsDigit) {
      return letters;
    }

    if (!second) {
      return `${letters}${first}`;
    }

    const secondIsDigit = /\d/.test(second);
    const secondIsLetter = /[A-Z]/.test(second);

    if (secondIsDigit) {
      const conventionalNumbers = `${first}${second}${third}${fourth}`.replace(
        /[^0-9]/g,
        ''
      );

      return `${letters}-${conventionalNumbers.slice(0, 4)}`;
    }

    if (secondIsLetter) {
      let mercosul = `${letters}${first}${second}`;

      if (third && /\d/.test(third)) {
        mercosul += third;
      }

      if (fourth && /\d/.test(fourth)) {
        mercosul += fourth;
      }

      return mercosul.slice(0, 7);
    }

    return `${letters}${first}`;
  }
}